'use client';

import { useCallback, useEffect, useState } from 'react';

import { UseScrollFadeOptions } from './models';

const SCROLLABLE_THRESHOLD = 2;
const EDGE_THRESHOLD = 4;

export const useScrollFade = ({ axis = 'vertical', enabled = true }: UseScrollFadeOptions = {}) => {
    const [scroller, setScroller] = useState<HTMLElement | null>(null);
    const [showStart, setShowStart] = useState(false);
    const [showEnd, setShowEnd] = useState(false);

    const update = useCallback(() => {
        if (!scroller || !enabled) {
            setShowStart(false);
            setShowEnd(false);
            return;
        }

        const isVertical = axis === 'vertical';
        const maxScroll = isVertical ? scroller.scrollHeight - scroller.clientHeight : scroller.scrollWidth - scroller.clientWidth;
        const offset = Math.abs(isVertical ? scroller.scrollTop : scroller.scrollLeft);
        const canScroll = maxScroll > SCROLLABLE_THRESHOLD;

        setShowStart(canScroll && offset > EDGE_THRESHOLD);
        setShowEnd(canScroll && offset < maxScroll - EDGE_THRESHOLD);
    }, [axis, enabled, scroller]);

    useEffect(() => {
        if (!scroller || !enabled) {
            update();
            return;
        }

        update();

        const resizeObserver = new ResizeObserver(() => update());
        const observeContent = () => {
            resizeObserver.disconnect();
            resizeObserver.observe(scroller);
            Array.from(scroller.children).forEach((child) => resizeObserver.observe(child));
        };

        observeContent();

        const mutationObserver = new MutationObserver(() => {
            observeContent();
            update();
        });
        mutationObserver.observe(scroller, { childList: true });

        scroller.addEventListener('scroll', update, { passive: true });

        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
            scroller.removeEventListener('scroll', update);
        };
    }, [enabled, scroller, update]);

    return { scrollerRef: setScroller, showStart, showEnd };
};
